import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getAxiosBaseURL } from '../config/apiConfig';
import { message } from 'antd';

// Configure axios baseURL using central config
const AXIOS_BASE_URL = getAxiosBaseURL();
if (AXIOS_BASE_URL) {
  axios.defaults.baseURL = AXIOS_BASE_URL;
}

interface User {
  userId: number;
  username: string;
  fullName: string;
  email?: string;
  roleId: number;
  roleName: string;
  roleKey: string;
  avatar?: string;
  permissions?: any[];
}

interface Permission {
  modulekey: string;
  module: string;
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
  all: boolean;
}

interface AuthContextType {
  user: User | null;
  permissions: Permission[];
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Token storage key - match with AuthService
const TOKEN_KEY = 'quanly_truonghoc_token';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      console.log('🔍 Auth check starting...', {
        hasToken: !!token,
        currentURL: window.location.href,
        axiosBaseURL: axios.defaults.baseURL,
        hostname: window.location.hostname
      });
      
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Verify token and get user info
        const response = await axios.get('/api/v1/auth/me');
        console.log('📡 /me response:', {
          status: response.status,
          success: response.data?.success,
          hasData: !!response.data?.data
        });
        
        if (response.data.success) {
          // /me endpoint returns data in response.data.data
          setUser(response.data.data);
          // For permissions, we'll get them separately or from user data
          setPermissions([]);
          console.log('✅ Auth check successful');
        } else {
          console.log('❌ Auth check failed - removing token');
          localStorage.removeItem(TOKEN_KEY);
          delete axios.defaults.headers.common['Authorization'];
        }
      } else {
        console.log('ℹ️  No token found - user not authenticated');
      }
    } catch (error: any) {
      console.error('💥 Auth check failed:', error);
      console.error('📡 Error response:', error.response?.data);
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      console.log('🔍 Login attempt starting...');
      console.log('🌐 Current URL:', window.location.href);
      console.log('🔌 Axios baseURL:', axios.defaults.baseURL);
      console.log('🏠 Hostname:', window.location.hostname);
      
      const response = await axios.post('/api/v1/auth/login', {
        username,
        password
      });

      console.log('📡 Login response received:', {
        status: response.status,
        success: response.data?.success,
        hasToken: !!response.data?.token,
        tokenLength: response.data?.token?.length,
        hasUser: !!response.data?.user
      });

      if (response.data.success) {
        const { token, user: userData, rolePermissions, userPermissions } = response.data;
        
        console.log('💾 Saving token to localStorage...');
        localStorage.setItem(TOKEN_KEY, token);
        console.log('✅ Token saved:', localStorage.getItem(TOKEN_KEY) ? 'Success' : 'Failed');
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('🔑 Authorization header set');
        
        setUser(userData);
        setPermissions(userPermissions || []);
        
        console.log('👤 User state updated:', userData?.username);
        console.log('🚀 Login process completed - should redirect now');
        
        message.success('Đăng nhập thành công');
        return true;
      } else {
        console.log('❌ Login failed:', response.data.message);
        message.error(response.data.message || 'Đăng nhập thất bại');
        return false;
      }
    } catch (error: any) {
      console.error('💥 Login error:', error);
      console.error('📡 Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Lỗi kết nối server';
      message.error(errorMessage);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setPermissions([]);
    message.success('Đăng xuất thành công');
  };

  const updateUser = (userData: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...userData } : null);
  };

  const value: AuthContextType = {
    user,
    permissions,
    login,
    logout,
    isAuthenticated: !!user,
    loading,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };
export default AuthProvider;
